// Dynamics band mapping — shared between the velocity-meter component and the
// scoring/judging code.
//
// This module deliberately has NO Lit (or any) imports. `velocity-meter.ts` lives
// inside the components bundle, while `conductor.ts`, `test-mode/*` and
// `practice-mode/*` live outside it and cannot import runtime values from bundled
// component files (their bare `lit` imports are unresolvable in the browser).
// Keeping the table here is what lets both sides share one source of truth.

import { DynamicValue } from './shared/alphatab-enums.js';

/** Velocity span (inclusive) that a dynamics marking nominally represents. */
export interface DynamicsBand {
    min: number;
    max: number;
    label: string;
    /** Plain-English gloss shown to the player, e.g. mp -> 'a little soft'. */
    meaning: string;
}

/**
 * Nominal velocity band per ordinary dynamics level. This is the DISPLAY mapping
 * used by the velocity meter's target marker; the JUDGING range is wider — see
 * `allowedRange()`.
 */
export const DYNAMICS_RANGES: Record<number, DynamicsBand> = {
    [DynamicValue.PPP]: { min: 1, max: 20, label: 'ppp', meaning: 'as soft as possible' },
    [DynamicValue.PP]: { min: 21, max: 35, label: 'pp', meaning: 'very soft' },
    [DynamicValue.P]: { min: 36, max: 50, label: 'p', meaning: 'soft' },
    [DynamicValue.MP]: { min: 51, max: 65, label: 'mp', meaning: 'a little soft' },
    [DynamicValue.MF]: { min: 66, max: 80, label: 'mf', meaning: 'a little loud' },
    [DynamicValue.F]: { min: 81, max: 95, label: 'f', meaning: 'loud' },
    [DynamicValue.FF]: { min: 96, max: 110, label: 'ff', meaning: 'very loud' },
    [DynamicValue.FFF]: { min: 111, max: 127, label: 'fff', meaning: 'as loud as possible' },
};

/** The ordinary graded levels, softest to loudest. Index === DynamicValue 0-7. */
export const ORDERED_BANDS: DynamicValue[] = [
    DynamicValue.PPP,
    DynamicValue.PP,
    DynamicValue.P,
    DynamicValue.MP,
    DynamicValue.MF,
    DynamicValue.F,
    DynamicValue.FF,
    DynamicValue.FFF,
];

/**
 * Map any DynamicValue onto one of the eight ordinary bands, or null if it is not
 * a point on the loudness scale.
 *
 * - 0-7 pass through.
 * - Extended levels collapse to the nearest ordinary extreme (pppp+ -> ppp,
 *   ffff+ -> fff).
 * - `pf` (poco forte) is a genuine level between mf and f; treated as f.
 * - Accent/composite marks (sf, sfz, fp, n, ...) return null. They describe an
 *   attack or a shape, not a level, so guessing a band for them would produce
 *   false warnings. Callers exempt them — see `buildExpectedBands()`.
 *
 * Values outside the known enum return null rather than silently becoming mf.
 */
export function normalizeDynamics(dynamics: DynamicValue): DynamicValue | null {
    if (dynamics >= DynamicValue.PPP && dynamics <= DynamicValue.FFF) {
        return dynamics;
    }
    switch (dynamics) {
        case DynamicValue.PPPP:
        case DynamicValue.PPPPP:
        case DynamicValue.PPPPPP:
            return DynamicValue.PPP;
        case DynamicValue.FFFF:
        case DynamicValue.FFFFF:
        case DynamicValue.FFFFFF:
            return DynamicValue.FFF;
        case DynamicValue.PF:
            return DynamicValue.F;
        default:
            // sf, sfp, sfpp, fp, rf, rfz, sfz, sffz, fz, n, sfzp, and anything
            // unrecognised: not a loudness level.
            return null;
    }
}

/** True when a DynamicValue is an accent/composite mark rather than a level. */
export function isAccentMark(dynamics: DynamicValue): boolean {
    return normalizeDynamics(dynamics) === null;
}

/** Display label for a dynamics value ('--' when it maps to no band). */
export function dynamicsLabel(dynamics: DynamicValue): string {
    const band = normalizeDynamics(dynamics);
    if (band === null) return '--';
    return DYNAMICS_RANGES[band]?.label ?? '--';
}

/**
 * Plain-English gloss for a dynamics value, for player-facing text. Returns '' when the
 * value maps to no band (an accent mark), so a caller can omit the phrase rather than
 * print a placeholder at the player.
 */
export function dynamicsMeaning(dynamics: DynamicValue): string {
    const band = normalizeDynamics(dynamics);
    if (band === null) return '';
    return DYNAMICS_RANGES[band]?.meaning ?? '';
}

/** How many bands of slack we grant around a written level. Applied uniformly. */
export const TOLERANCE_BANDS = 2;

/**
 * The velocity range a press may occupy without being flagged: the band as
 * written, widened by TOLERANCE_BANDS full bands in each direction and clamped
 * at the ends of the scale. So mf (66-80) accepts 36-110 — p's floor through
 * ff's ceiling.
 *
 * Two bands of slack, not one, is deliberate. Two forces stack:
 *   1) Velocity output is a property of the keyboard, not the player. The same
 *      musical mf on a light-action controller and a weighted hammer-action
 *      piano can differ by 20+ units.
 *   2) A chord onset aggregates over voices played at deliberately different
 *      loudnesses (voicing). Even soft playing spreads across ~30 units within
 *      one chord, and the whole onset is judged by that spread's average — see
 *      `finalizeOnsetVerdict` in dynamics-judge.ts.
 * One band of slack was too tight on both counts. Two produces the fewest false
 * warnings while still flagging playing that is 3+ dynamic steps off, which is
 * what we want to tell the player about.
 *
 * `band` must be an ordinary level (0-7), i.e. the output of normalizeDynamics().
 */
export function allowedRange(band: DynamicValue): { min: number; max: number } {
    const idx = ORDERED_BANDS.indexOf(band);
    if (idx < 0) {
        // Defensive: caller passed an un-normalized value. Fall back to the
        // band's own edges rather than inventing a range.
        const own = DYNAMICS_RANGES[band];
        return own ? { min: own.min, max: own.max } : { min: 1, max: 127 };
    }
    const lastIdx = ORDERED_BANDS.length - 1;
    const lo = ORDERED_BANDS[Math.max(0, idx - TOLERANCE_BANDS)];
    const hi = ORDERED_BANDS[Math.min(lastIdx, idx + TOLERANCE_BANDS)];
    return { min: DYNAMICS_RANGES[lo].min, max: DYNAMICS_RANGES[hi].max };
}

/** Verdict for one onset's loudest correct-pitch press. */
export type DynamicsVerdict = 'in-range' | 'too-loud' | 'too-soft' | 'exempt';

/**
 * Judge a velocity against an expected band. `expectedBand === null` means the
 * onset is exempt (hairpin, accent mark, or ineligible slice).
 */
export function judgeVelocity(
    velocity: number,
    expectedBand: DynamicValue | null
): DynamicsVerdict {
    if (expectedBand === null) return 'exempt';
    const { min, max } = allowedRange(expectedBand);
    if (velocity > max) return 'too-loud';
    if (velocity < min) return 'too-soft';
    return 'in-range';
}

// ============================================
// Penalty tuning
// ============================================

/**
 * Worst-case points deducted from a 0-100 headline score when every judged onset
 * was out of range. Dynamics matter meaningfully but pitch and timing matter more:
 * the hit% and precision% legs together supply 100 points to the score, so a full
 * dynamics penalty is worth about 30% of one of those legs. Historical rows written
 * before this constant changed carry their original penalty in the stored score; new
 * rows will land on a different scale, and the history calendar's derived "-N pts"
 * line reflects the current cap for all rows.
 */
export const MAX_DYN_PENALTY = 30;

/**
 * Judged-onset count at which the penalty reaches full weight. Below this the
 * penalty is scaled down proportionally, so a pass that is mostly hairpin-exempt
 * (Moonlight is ~62% hairpin) or a two-bar loop cannot lose the full 15 points off
 * a three-onset sample — and the live score eases in instead of jumping.
 */
export const MIN_JUDGED_ONSETS = 8;

/**
 * Points to deduct for dynamics. Returns 0 when nothing was judged, which is also
 * the value for an ineligible slice or a disabled setting — that is what keeps
 * every historical performance on the same 0-100 scale.
 */
export function dynamicsPenalty(judgedOnsets: number, outOfRangeOnsets: number): number {
    if (judgedOnsets <= 0) return 0;
    const rate = outOfRangeOnsets / judgedOnsets;
    const ramp = Math.min(1, judgedOnsets / MIN_JUDGED_ONSETS);
    return Math.round(rate * MAX_DYN_PENALTY * ramp);
}
